import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import SearchableSelect from '../common/SearchableSelect';
import { DeliveryChallanService, SalesOrderService } from '../../services/salesOrder.service';
import { CHALLAN_STATUS_COLORS, ChallanStatus, ChallanSummary } from '../../interfaces/sales/salesOrder.interface';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { humanizeEnum } from '../../utils/format';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { toastSaved } from '../../utils/fcDocuments';   // G5b

const STATUSES: ChallanStatus[] = ['DRAFT', 'POSTED', 'CANCELLED'];

/** Delivery challans; with ?status=DRAFT this is the store's "Challans to Issue" worklist. */
const DeliveryChallanList: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? '';
  const canInvoice = usePermission(PERMISSIONS.SALES_ORDER_MANAGE);
  const institutionId = AuthService.getSelectedInstitution();
  const [rows, setRows] = useState<ChallanSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState<string[]>([]);
  const [invoiceOpen, setInvoiceOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setRows(await DeliveryChallanService.list(institutionId, { status: (status || undefined) as ChallanStatus | undefined }));
      setPicked([]);
    } catch (e) {
      toast.showApiError(e, 'Failed to load delivery challans');
    } finally {
      setLoading(false);
    }
  }, [institutionId, status]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const invoiceable = (c: ChallanSummary) => c.status === 'POSTED' && !c.salesInvoiceId;
  const pickedRows = rows.filter(r => picked.includes(r.id));
  const oneCustomer = new Set(pickedRows.map(r => r.customerId)).size === 1;

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.challanList.page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Typography variant="h5">{status === 'DRAFT' ? 'Challans to Issue' : 'Delivery Challans'}</Typography>
        {canInvoice && (
          <Button data-help="sales.challanList.createInvoice" variant="contained" disabled={pickedRows.length === 0 || !oneCustomer} onClick={() => setInvoiceOpen(true)}>
            Create invoice from selected
          </Button>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <SearchableSelect
          data-help="sales.challanList.statusFilter"
          label="Status" size="small" sx={{ flex: '1 1 220px', maxWidth: 320 }} options={STATUSES.map(s => ({ id: s, name: humanizeEnum(s) }))}
          value={status || null} onChange={id => setParams(id ? { status: id } : {})} getOptionLabel={o => o.name} clearable
        />
      </Box>
      {pickedRows.length > 0 && !oneCustomer && <Typography color="error" sx={{ mb: 1 }}>Pick challans of one customer only.</Typography>}
      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper} data-help-section="sales.challanList.table">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" data-help="sales.challanList.col.pick" /><TableCell data-help="sales.challanList.col.number">Challan #</TableCell><TableCell data-help="sales.challanList.col.order">Order</TableCell><TableCell data-help="sales.challanList.col.customer">Customer</TableCell>
                <TableCell data-help="sales.challanList.col.date">Date</TableCell><TableCell data-help="sales.challanList.col.store">Store</TableCell><TableCell data-help="sales.challanList.col.status">Status</TableCell><TableCell data-help="sales.challanList.col.invoice">Invoice</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(c => (
                <TableRow key={c.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/sales/challans/${c.id}`)}>
                  <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                    <Checkbox data-help="sales.challanList.action.pick" disabled={!invoiceable(c)} checked={picked.includes(c.id)}
                      onChange={e => setPicked(p => (e.target.checked ? [...p, c.id] : p.filter(x => x !== c.id)))} />
                  </TableCell>
                  <TableCell>{c.challanNo ?? 'Draft'}</TableCell><TableCell>{c.orderNo}</TableCell><TableCell>{c.customerName}</TableCell>
                  <TableCell>{formatDate(c.challanDate)}</TableCell><TableCell>{c.storeName}</TableCell>
                  <TableCell><Chip data-help="sales.challanList.status" size="small" label={humanizeEnum(c.status)} color={CHALLAN_STATUS_COLORS[c.status]} /></TableCell>
                  <TableCell>{c.invoiceNumber ?? (c.salesInvoiceId ? 'Draft invoice' : '-')}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={8}>No challans.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {invoiceOpen && (
        <InvoiceFromChallansDialog institutionId={institutionId} challanIds={picked} onClose={() => setInvoiceOpen(false)}
          onCreated={id => navigate(`/sales/invoices/${id}`)} />
      )}
    </Box>
  );
};

const InvoiceFromChallansDialog: React.FC<{ institutionId: string; challanIds: string[]; onClose: () => void; onCreated: (id: string) => void }> =
  ({ institutionId, challanIds, onClose, onCreated }) => {
    const toast = useToast();
    const [issueDate, setIssueDate] = useState(toLocalIsoDate());
    const [dueDate, setDueDate] = useState('');
    const [rate, setRate] = useState('');   // G5b: only a foreign-currency order uses it
    const [saving, setSaving] = useState(false);
    const create = async () => {
      try {
        setSaving(true);
        const inv = await SalesOrderService.invoiceFromChallans(institutionId, {
          challanIds, issueDate, dueDate: dueDate || undefined, exchangeRate: Number(rate) > 0 ? Number(rate) : null,
        });
        toastSaved(toast, 'Draft invoice created from the challans', inv.warnings);   // G5b
        onCreated(inv.id);
      } catch (e) {
        toast.showApiError(e, 'Failed to create the invoice');
      } finally {
        setSaving(false);
      }
    };
    return (
      <Dialog open onClose={onClose} fullWidth maxWidth="xs">
        <DialogTitle>Invoice {challanIds.length} challan(s)</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField data-help="sales.challanList.dialog.issueDate" label="Issue date" type="date" value={issueDate} onChange={e => setIssueDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField data-help="sales.challanList.dialog.dueDate" label="Due date (optional)" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField data-help="sales.challanList.dialog.rate" label="Exchange rate (optional)" type="number" value={rate} onChange={e => setRate(e.target.value)}
            inputProps={{ min: 0, step: 'any' }}
            helperText="Orders in a foreign currency are invoiced in that currency at this rate (BDT per unit); blank = the exchange-rate table on the issue date. Ignored for BDT orders." />
        </DialogContent>
        <DialogActions>
          <Button data-help="sales.challanList.dialog.cancel" onClick={onClose}>Cancel</Button>
          <Button data-help="sales.challanList.dialog.create" variant="contained" disabled={saving} onClick={create}>Create draft invoice</Button>
        </DialogActions>
      </Dialog>
    );
  };

export default DeliveryChallanList;
